/**
 * What a link opens: one meal, for whoever was sent it.
 *
 * The only screen in this app that renders for somebody with no account, which
 * makes it the only one that has to be right about a stranger. Three things
 * follow from that and none of them are cosmetic:
 *
 * **It is not the app.** No navigation, no ring, no day — a page, not a
 * destination inside a product the reader may never have heard of. It is routed
 * outside the session gate for the same reason: everything else in `App` asks
 * the API who you are first, and here nobody is.
 *
 * **It is identical for everyone until the footer.** The meal reads the same
 * whoever opened it; the last third is the only part that knows whether you
 * have an account. A stranger can save the recipe if they sign up. A household
 * member logs the portion into their own dinner in one tap — and the sender's
 * pot is untouched, because portions eaten by somebody else's diary did not
 * come out of your store.
 *
 * **A closed link says so, and says nothing else.** Revoked and expired are one
 * message, deliberately: which of the two happened is the sender's business,
 * and a closed link should not leak the meal it used to describe.
 *
 * The two questions a log needs are the only two asked — how much, and which
 * meal — with one portion and the meal the clock suggests already chosen, so
 * the common case is one tap. Half and one-and-a-half exist because a portion
 * is a serving suggestion rather than a rule, and "Weigh it" falls back to the
 * pad for anyone who took a bowl and put the rest back.
 */

import type { ApiError } from "@calorio/api-client";
import {
  formatPortions,
  helpingBody,
  helpingFor,
  ONE_PORTION,
  useLogSharedMeal,
  useSaveSharedRecipe,
  useSharedMeal,
  type Helping,
  type Meal,
} from "@calorio/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { HelpingPicker } from "../components/HelpingPicker";
import { mealForHour } from "../components/MealPicker";
import { useAnonymousCalorio } from "../app/providers";
import { useSession } from "../app/session";

const MEALS: Meal[] = ["breakfast", "lunch", "dinner", "snack"];
const LABEL: Record<Meal, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};
const IN_MEAL: Record<Meal, string> = {
  breakfast: "breakfast",
  lunch: "lunch",
  dinner: "dinner",
  snack: "snacks",
};

const round = (value: number) => Math.round(value).toLocaleString();
const day = (date: string) =>
  new Date(`${date}T12:00:00Z`).toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

export function SharedMeal() {
  const { token = "" } = useParams<{ token: string }>();
  // Not `useCalorio`: that one fetches the profile for its timezone, and this
  // page opens for people who have no profile to fetch.
  const ctx = useAnonymousCalorio();
  const { status } = useSession();
  const navigate = useNavigate();
  const meal = useSharedMeal(ctx, token || null);
  const log = useLogSharedMeal(ctx, token);
  const save = useSaveSharedRecipe(ctx, token);

  const [helping, setHelping] = useState<Helping>(ONE_PORTION);
  const [padUp, setPadUp] = useState(false);
  const [into, setInto] = useState<Meal>(() => mealForHour(new Date().getHours()));
  const [done, setDone] = useState(false);

  if (meal.isLoading) {
    return (
      <main className="shared">
        <p className="muted">…</p>
      </main>
    );
  }

  if (!meal.data) {
    // 404 and 410 are one page. The distinction exists on the server so a
    // monitor can tell a dead link from a bad URL; a person holding one only
    // needs to know it does not work any more.
    const failure = (meal.error as ApiError | undefined)?.status;
    return (
      <main className="shared shared--closed">
        <header className="shared__brand">Calorio</header>
        <h1 className="shared__title">This link is closed</h1>
        <p className="muted">
          {failure === 404
            ? "Nothing was ever shared at this address."
            : "Whoever sent it has turned it off, or it reached its date. Nothing about the meal is readable here any more."}
        </p>
        <p className="muted">
          Ask them for a new one — a fresh link takes them one tap and does not resurrect this
          address.
        </p>
      </main>
    );
  }

  const shared = meal.data;
  const portions = Math.max(1, shared.portions_made);
  const headMeta = [
    `makes ${portions}`,
    shared.weighed && shared.portion_grams ? `${Math.round(shared.portion_grams)} g each` : null,
    shared.pot ? `${formatPortions(shared.pot.portions_remaining)} left` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const per100Line = shared.kcal_per_100_cooked
    ? `${Math.round(shared.kcal_per_100_cooked)} kcal / 100 g cooked`
    : `No cooked weight — a portion is one ${portions}th of the pot`;

  const rawNote = shared.weighed
    ? `Raw weights, before ${round(
        (shared.raw_weight_g ?? 0) - (shared.cooked_weight_g ?? 0),
      )} g cooked off. The per-portion figures above are measured against the cooked pot.`
    : `Raw weights. The pot was not weighed after cooking, so the per-portion figures are one ${portions}th of these ingredients rather than a measured serving.`;

  const footNote = shared.include_ingredients
    ? "Figures come from the ingredients as weighed, not from a database of chillis. Nobody checked them but the person who cooked it."
    : shared.weighed
      ? "The person who shared this kept the ingredients to themselves. The per-portion figures are theirs, measured off the pot they cooked."
      : `The person who shared this kept the ingredients to themselves. The per-portion figures are theirs, worked out from what went in and divided by ${portions} — the pot itself was not weighed.`;

  const helped = helpingFor(shared, helping);
  const logIt = (chosen: Helping) =>
    log.mutate({ meal: into, ...helpingBody(chosen) }, { onSuccess: () => setDone(true) });

  return (
    <main className="shared">
      <header className="shared__brand">
        Calorio <span className="muted">Shared meal</span>
      </header>

      <h1 className="shared__title">{shared.name}</h1>
      <p className="muted num">{headMeta}</p>

      <section className="shared__portion">
        <span className="shared__portion-head">One portion</span>
        <span className="shared__kcal num">
          {round(shared.kcal_per_portion ?? 0)} <small>kcal</small>
          {shared.weighed && shared.portion_grams ? (
            <small className="muted"> · {Math.round(shared.portion_grams)} g</small>
          ) : null}
        </span>
        <dl className="shared__macros num">
          <div>
            <dt>Protein</dt>
            <dd>{Math.round(shared.protein_g_per_portion ?? 0)}</dd>
          </div>
          <div>
            <dt>Carbs</dt>
            <dd>{Math.round(shared.carbs_g_per_portion ?? 0)}</dd>
          </div>
          <div>
            <dt>Fat</dt>
            <dd>{Math.round(shared.fat_g_per_portion ?? 0)}</dd>
          </div>
        </dl>
        <p className="muted">{per100Line}</p>
      </section>

      {shared.pot && (
        <section className="shared__pot">
          <h2 className="shared__heading">Stored now</h2>
          <p className="num">
            <strong>{formatPortions(shared.pot.portions_remaining)}</strong> of{" "}
            {shared.pot.portions_made} portions left
          </p>
          <p className="muted">
            cooked {day(shared.pot.cooked_date)}
            {shared.pot.best_before ? ` · best before ${day(shared.pot.best_before)}` : ""}
          </p>
        </section>
      )}

      {shared.include_ingredients && shared.ingredients.length > 0 && (
        <section className="shared__ingredients">
          <h2 className="shared__heading">What went in</h2>
          <ul>
            {shared.ingredients.map((line, index) => (
              <li key={`${line.name}:${index}`}>
                <span>{line.name}</span>
                <span className="num">{round(line.grams)} g</span>
              </li>
            ))}
          </ul>
          <p className="muted">{rawNote}</p>
        </section>
      )}

      <p className="muted shared__foot">{footNote}</p>

      {/* The only part of the page that knows whether you have an account. */}
      {status !== "signed-in" ? (
        <section className="shared__cta">
          <a className="shared__go" href="/">
            Save this to my foods
          </a>
          <p className="muted">Takes an account. Nothing is logged by opening this.</p>
        </section>
      ) : done ? (
        <section className="shared__cta">
          <p>
            Logged to {IN_MEAL[into]}. <a href="/">Open your day</a>
          </p>
        </section>
      ) : (
        <section className="shared__cta">
          <h2 className="shared__heading">How much did you have</h2>
          {/* The same picker as the pot's own page: fractions of a portion,
              a typed number, and the scales only when the pot was weighed.
              The pad's key logs straight away — typing a weight and pressing
              "Log it" is one gesture, not two. */}
          <HelpingPicker
            source={shared}
            name={shared.name}
            value={helping}
            onChange={setHelping}
            padAction="Log it"
            onCommit={logIt}
            onPad={setPadUp}
          />

          {!padUp && (
            <>
              <div className="shared__meals">
                {MEALS.map((option) => (
                  <button
                    key={option}
                    className={`pot__chip${into === option ? " is-on" : ""}`}
                    onClick={() => setInto(option)}
                  >
                    {LABEL[option]}
                  </button>
                ))}
              </div>

              <p className="muted">
                Logging this does not touch the sender&rsquo;s store. Their count is what they have
                eaten, not what you have.
              </p>

              <button
                className="pot__go"
                disabled={log.isPending || helped === null}
                onClick={() => logIt(helping)}
              >
                {log.isPending
                  ? "Logging…"
                  : `Log ${round(helped?.kcal ?? 0)} kcal to ${IN_MEAL[into]}`}
              </button>

              {shared.include_ingredients && (
                <div className="shared__extra">
                  <button
                    className="pot__chip"
                    disabled={save.isPending}
                    onClick={() => save.mutate()}
                  >
                    {save.isSuccess ? "Saved" : "Save the recipe"}
                  </button>
                  <button
                    className="pot__chip"
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutateAsync().then(
                        () => navigate("/"),
                        () => undefined,
                      )
                    }
                  >
                    Cook it myself
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}
    </main>
  );
}
